import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import ResendVerification from './components/ResendVerification';
import ForgotPassword from './components/ForgotPassword';
import LandingPage from './components/LandingPage';
import ChangePassword from './components/ChangePassword';
import EmailVerification from './components/EmailVerification';
import ResetPasswordWithToken from './components/ResetPasswordWithToken';
import CourseReview from './components/CourseReview';
import CourseReviewDetail from './components/CourseReviewDetail';
import MyPosts from './components/MyPosts';
import MarketplaceDetailPage from './components/MarketplaceDetailPage';
import MeinMarktplatz from './components/MeinMarktplatz'; // Issue #65
import SellerContact from './components/SellerContact'; // Issue #69
import Chat from './components/Chat'; // Issue #69
import ChatList from './components/ChatList'; // Issue #91, #92, #93
import NotificationList from './components/NotificationList'; // Issue #87: Notifications
import Profile from './components/Profile'; // Issue #84
import LanguageSettings from './components/LanguageSettings'; // Issue #79
import DisplaySettings from './components/DisplaySettings'; // Issue #98: Dark Mode
import NotificationSettings from './components/NotificationSettings'; // Issue #88: Notification preferences
import AccountSettings from './components/AccountSettings'; // Issue #137: Account settings
import DeleteAccount from './components/DeleteAccount'; // Issue #137: Delete account flow
import CreatePostPage from './components/CreatePostPage'; // Issue #101: Create post page
import CreateMarketplaceObjectPage from './components/CreateMarketplaceObjectPage'; // Issue #101: Create marketplace page
import PrivacyPolicy, { AuthenticatedPrivacyPolicy } from './components/PrivacyPolicy'; // Issue #68: Privacy policy page
import TermsAndConditions, { AuthenticatedTermsAndConditions } from './components/TermsAndConditions'; // Issue #118: Terms and conditions page
import { AuthenticatedCommunityGuideline } from './components/CommunityGuideline'; // Issue #149: Community guideline page
import { NavigationProvider } from './contexts/NavigationContext'; // Issue #89: Navigation context
import { GlobalModalProvider } from './contexts/GlobalModalContext'; // Global modal context
import { LanguageProvider } from './contexts/LanguageContext'; // Issue #79: Language context
import { ThemeProvider } from './contexts/ThemeContext'; // Issue #98: Dark Mode
import { AdProvider } from './contexts/AdContext'; // Issue #204: Google Ads
import { ConsentDialog, ConsentConfirmationDialog, InterstitialAd } from './components/ads'; // Issue #204: GDPR consent, Issue #275: Confirmation popover, Issue #311: Interstitial ads
import MarketplaceModal from './components/MarketplaceModal'; // Global marketplace modal
import { LoadingPage, SuccessModal, AnnouncementDialog } from './components/shared'; // Issue #165: Loading page component, Issue #312: Share error modal, Issue #321: Announcements
import { configureAxios, mobileLogout } from './utils'; // Issue #90: Capacitor mobile support
import pushNotificationService from './services/pushNotificationService'; // Issue #87: Push notifications
import './App.css';

// Issue #90: Configure axios for web/mobile platform
// - On web: Uses cookies (withCredentials)
// - On mobile: Uses Authorization header with session token
configureAxios();

function App() {
  const [currentPage, setCurrentPage] = useState('login');
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showLoadingPage, setShowLoadingPage] = useState(false); // Issue #183: Only show loading page after 1 second delay
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedMarketplaceObject, setSelectedMarketplaceObject] = useState(null); // Issue #59: Selected marketplace object
  const [isOtpFlow, setIsOtpFlow] = useState(false); // Issue #18: Track if user logged in with OTP
  const [landingPageTab, setLandingPageTab] = useState('new'); // Track which tab to show on landing page
  // Issue #216: Initialize marketplace filter from localStorage to persist across page refreshes
  const [marketplaceFilter, setMarketplaceFilter] = useState(() => {
    return localStorage.getItem('marketplaceFilter') || 'offer';
  }); // Track marketplace filter ('offer' or 'search')
  const [meinMarktplatzTab, setMeinMarktplatzTab] = useState('favorites'); // Issue #71: Track MeinMarktplatz tab ('favorites' or 'myObjects')
  const [courseReviewFilters, setCourseReviewFilters] = useState({ degree: '', study_programs: [], language: '', semester: '', has_reviews: false, my_reviews: false, min_rating: 0 }); // Persist course review filters (Issue #78: rating filters)
  const [courseReviewSortBy, setCourseReviewSortBy] = useState(''); // Persist course review sort option
  const [autoOpenCreatePost, setAutoOpenCreatePost] = useState(false); // Issue #84: Auto-open create post dialog
  const [autoOpenCreateMarketplace, setAutoOpenCreateMarketplace] = useState(false); // Issue #84: Auto-open create marketplace dialog
  const [isRestoringFromHistory, setIsRestoringFromHistory] = useState(false); // Flag to prevent duplicate history entries
  const [selectedConversationId, setSelectedConversationId] = useState(null); // Issue #69: Selected conversation for chat
  const [chatFromSellerContact, setChatFromSellerContact] = useState(false); // Issue #430: When chat is opened by sending a first message from SellerContact, "back" must return to the chat list, not the contact-seller page
  const [changePasswordReturnPage, setChangePasswordReturnPage] = useState('landing'); // Track where to return from ChangePassword
  const [openCommentsForPostId, setOpenCommentsForPostId] = useState(null); // Issue #87: Open comments for specific post from notification (LandingPage)
  const [myPostsOpenCommentsForPostId, setMyPostsOpenCommentsForPostId] = useState(null); // Issue #177: Open comments for specific post in MyPosts
  const [pendingNotificationNav, setPendingNotificationNav] = useState(null); // Store notification navigation until session is verified
  const [resendVerificationEmail, setResendVerificationEmail] = useState(() => sessionStorage.getItem('resendVerificationEmail') || ''); // Issue #15: Email for resend verification page
  const [showInterstitialAd, setShowInterstitialAd] = useState(false); // Issue #311: Interstitial ad on first daily app open
  const [pendingShareNav, setPendingShareNav] = useState(null); // Issue #312: Store share navigation until session is verified
  const [shareAccessError, setShareAccessError] = useState(null); // Issue #312: Error message for share access denial
  const [sharedPost, setSharedPost] = useState(null); // Issue #317: Store shared post to display at top of feed

  // Issue #183: Only show loading page if session check takes more than 1 second
  // This prevents the brief flash of loading page during fast session checks
  useEffect(() => {
    if (isCheckingSession) {
      // Start a timer to show loading page after 1 second
      const timer = setTimeout(() => {
        setShowLoadingPage(true);
      }, 1000);

      // Cleanup: clear timer if session check completes before 1 second
      return () => clearTimeout(timer);
    } else {
      // Session check complete - hide loading page
      setShowLoadingPage(false);
    }
  }, [isCheckingSession]);

  // Issue #311: Show interstitial ad on first daily app open (only for authenticated users)
  useEffect(() => {
    if (!isCheckingSession && currentPage !== 'login' && currentPage !== 'register' &&
        currentPage !== 'forgotPassword' && currentPage !== 'verifyEmail' &&
        currentPage !== 'resendVerification' && currentPage !== 'resetPassword') {
      // Small delay to ensure the user sees the main content first
      const timer = setTimeout(() => {
        setShowInterstitialAd(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isCheckingSession, currentPage]);

  // Process pending notification navigation after session is verified
  useEffect(() => {
    if (!isCheckingSession && pendingNotificationNav && currentPage !== 'login') {
      console.log('[PUSH] Processing pending notification navigation:', pendingNotificationNav);
      const { referenceType, referenceId } = pendingNotificationNav;

      if (referenceType && referenceId) {
        switch (referenceType) {
          case 'conversation':
            setSelectedConversationId(referenceId);
            setCurrentPage('chat');
            break;
          case 'post':
            // Issue #177: Navigate to MyPosts for comment notifications
            setMyPostsOpenCommentsForPostId(referenceId);
            setCurrentPage('myPosts');
            break;
          case 'marketplace':
            setSelectedMarketplaceObject({ id: referenceId });
            setCurrentPage('marketplaceDetail');
            break;
          default:
            setCurrentPage('notifications');
        }
      }
      setPendingNotificationNav(null);
    }
  }, [isCheckingSession, pendingNotificationNav, currentPage]);

  // Issue #312: Process pending share navigation after session is verified
  // Issue #317: Fixed to properly handle navigation to register/forgotPassword pages
  useEffect(() => {
    const processShareNav = async () => {
      // Issue #317: List of all unauthenticated pages where share navigation should wait
      const unauthenticatedPages = ['login', 'register', 'forgotPassword', 'verifyEmail', 'resendVerification', 'resetPassword', 'privacyPolicyPublic', 'termsAndConditionsPublic'];
      const isOnUnauthenticatedPage = unauthenticatedPages.includes(currentPage);

      if (!isCheckingSession && pendingShareNav && !isOnUnauthenticatedPage) {
        console.log('[SHARE] Processing pending share navigation:', pendingShareNav);
        const { type, id } = pendingShareNav;

        try {
          let response;
          switch (type) {
            case 'post':
              response = await axios.get(`/api/share/post/${id}`);
              // Issue #317: Store the shared post to display at top of feed
              setSharedPost(response.data);
              setLandingPageTab('new'); // Switch to 'new' tab to show posts
              setCurrentPage('landing');
              break;
            case 'object':
              response = await axios.get(`/api/share/object/${id}`);
              // Navigate to marketplace detail page
              setSelectedMarketplaceObject(response.data);
              setCurrentPage('marketplaceDetail');
              break;
            case 'course':
              response = await axios.get(`/api/share/course/${id}`);
              // Navigate to course review detail page
              setSelectedCourse(response.data);
              setCurrentPage('courseReviewDetail');
              break;
            default:
              console.log('[SHARE] Unknown share type:', type);
              setCurrentPage('landing');
          }
          setShareAccessError(null);
        } catch (err) {
          console.error('[SHARE] Error fetching shared content:', err);
          const errorField = err.response?.data?.field;
          const errorMessage = err.response?.data?.message;

          if (errorField === 'access_denied') {
            setShareAccessError(errorMessage || 'Leider kannst du nicht auf die Inhalte anderer Universitäten zugreifen.');
          } else if (errorField === 'not_found') {
            setShareAccessError(errorMessage || 'Der geteilte Inhalt wurde nicht gefunden.');
          } else {
            setShareAccessError('Fehler beim Laden des geteilten Inhalts.');
          }
          setCurrentPage('landing');
        }

        setPendingShareNav(null);
      } else if (!isCheckingSession && pendingShareNav && isOnUnauthenticatedPage) {
        // User needs to log in first - keep pendingShareNav for after login
        console.log('[SHARE] User on unauthenticated page, will process share after login');
      }
    };

    processShareNav();
  }, [isCheckingSession, pendingShareNav, currentPage]);

  // Save current page and related state to sessionStorage whenever they change
  // Also push to browser history for proper back button/swipe support
  useEffect(() => {
    if (!isCheckingSession) {
      const pageState = {
        currentPage,
        landingPageTab,
        marketplaceFilter,
        meinMarktplatzTab,
        courseReviewFilters,
        courseReviewSortBy,
        selectedCourse,
        selectedMarketplaceObject,
        selectedConversationId,
        chatFromSellerContact,
        isOtpFlow
      };

      // Save to sessionStorage for authenticated pages only
      if (currentPage !== 'login' && currentPage !== 'register' && currentPage !== 'forgotPassword' && currentPage !== 'verifyEmail' && currentPage !== 'resendVerification' && currentPage !== 'resetPassword') {
        sessionStorage.setItem('pageState', JSON.stringify(pageState));
      }

      // Push to browser history for ALL pages (including login/register)
      // Skip if we're restoring from history (to prevent duplicate entries)
      if (!isRestoringFromHistory) {
        const historyState = { ...pageState, timestamp: Date.now() };

        // For the very first history entry after app loads, use replaceState
        // For subsequent navigations, use pushState
        if (!window.history.state || window.history.state.timestamp === undefined) {
          // First entry - replace current history
          console.log('[HISTORY] Creating initial history entry for page:', currentPage);
          window.history.replaceState(historyState, '', window.location.pathname);
        } else if (window.history.state.currentPage !== currentPage) {
          // Different page - push new history entry
          console.log('[HISTORY] Pushing new history entry:', window.history.state.currentPage, '->', currentPage);
          window.history.pushState(historyState, '', window.location.pathname);
        } else {
          // Same page - just update the state
          console.log('[HISTORY] Updating current history entry for page:', currentPage);
          window.history.replaceState(historyState, '', window.location.pathname);
        }
      } else {
        console.log('[HISTORY] Skipping history push (restoring from back navigation)');
      }
    }
  }, [currentPage, landingPageTab, marketplaceFilter, meinMarktplatzTab, courseReviewFilters, courseReviewSortBy, selectedCourse, selectedMarketplaceObject, selectedConversationId, chatFromSellerContact, isOtpFlow, isCheckingSession, isRestoringFromHistory]);

  // Handle browser back button and swipe gestures
  useEffect(() => {
    const handlePopState = (event) => {
      console.log('[BACK NAVIGATION] popstate event fired', event.state);

      if (event.state && event.state.currentPage) {
        const targetPage = event.state.currentPage;
        console.log('[BACK NAVIGATION] Attempting to restore to page:', targetPage);

        // Prevent going back to login/register/auth pages when user is on authenticated pages
        const authPages = ['login', 'register', 'forgotPassword', 'verifyEmail', 'resendVerification', 'resetPassword'];
        const isTargetAuthPage = authPages.includes(targetPage);
        const currentPageIsAuthenticated = !authPages.includes(currentPage);

        if (isTargetAuthPage && currentPageIsAuthenticated) {
          console.log('[BACK NAVIGATION] Prevented going back to auth page. Pushing forward to maintain session.');
          // User is trying to go back to login while logged in
          // Push current state forward to prevent going back
          const currentState = {
            currentPage,
            landingPageTab,
            marketplaceFilter,
            meinMarktplatzTab,
            courseReviewFilters,
            courseReviewSortBy,
            selectedCourse,
            selectedMarketplaceObject,
            selectedConversationId,
            chatFromSellerContact,
            isOtpFlow,
            timestamp: Date.now()
          };
          window.history.pushState(currentState, '', window.location.pathname);
          return; // Don't restore to auth page
        }

        // Set flag to prevent duplicate history entry
        setIsRestoringFromHistory(true);

        // Restore page state from history
        const historyState = event.state;

        // Restore all dependent state FIRST before setting currentPage
        if (historyState.landingPageTab !== undefined) setLandingPageTab(historyState.landingPageTab);
        if (historyState.marketplaceFilter !== undefined) setMarketplaceFilter(historyState.marketplaceFilter);
        if (historyState.meinMarktplatzTab !== undefined) setMeinMarktplatzTab(historyState.meinMarktplatzTab);
        if (historyState.courseReviewFilters !== undefined) setCourseReviewFilters(historyState.courseReviewFilters);
        if (historyState.courseReviewSortBy !== undefined) setCourseReviewSortBy(historyState.courseReviewSortBy);
        if (historyState.selectedCourse !== undefined) setSelectedCourse(historyState.selectedCourse);
        if (historyState.selectedMarketplaceObject !== undefined) setSelectedMarketplaceObject(historyState.selectedMarketplaceObject);
        if (historyState.selectedConversationId !== undefined) setSelectedConversationId(historyState.selectedConversationId);
        if (historyState.chatFromSellerContact !== undefined) setChatFromSellerContact(historyState.chatFromSellerContact);
        if (historyState.isOtpFlow !== undefined) setIsOtpFlow(historyState.isOtpFlow);

        // Set currentPage LAST to ensure all dependent state is ready
        setCurrentPage(historyState.currentPage);

        // Reset flag after state updates
        setTimeout(() => setIsRestoringFromHistory(false), 0);
      } else {
        console.log('[BACK NAVIGATION] No state in history - user went back past app entry point');
        // No state in history - user went back past the first app entry
        // Allow browser to navigate away from the app (this is expected behavior)
      }
    };

    // Add event listener for back/forward navigation
    window.addEventListener('popstate', handlePopState);
    console.log('[BACK NAVIGATION] Popstate listener added');

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
      console.log('[BACK NAVIGATION] Popstate listener removed');
    };
  }, [currentPage, landingPageTab, marketplaceFilter, meinMarktplatzTab, courseReviewFilters, courseReviewSortBy, selectedCourse, selectedMarketplaceObject, selectedConversationId, isOtpFlow]); // Include current state in dependencies

  // PWA: Enable browser's native install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Don't prevent default - let browser show native install UI
      console.log('[PWA] Install prompt available');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  // Issue #87: Initialize push notification listeners on app mount
  // This sets up the handlers that will receive the FCM token and send it to backend
  useEffect(() => {
    if (!pushNotificationService.isPushNotificationsSupported()) {
      return;
    }

    console.log('[PUSH] Initializing push notification listeners');
    const cleanup = pushNotificationService.initializePushNotifications({
      onRegistration: (token) => {
        console.log('[PUSH] Token registered:', token.substring(0, 20) + '...');
      },
      onNotificationReceived: (notification) => {
        console.log('[PUSH] Foreground notification:', notification);
        // Could show an in-app notification here
      },
      onNotificationTapped: (data) => {
        console.log('[PUSH] Notification tapped, storing for navigation:', data);
        // Store the navigation data - it will be processed after session is verified
        setPendingNotificationNav(data);
      },
      onError: (error) => {
        console.error('[PUSH] Registration error:', error);
      }
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  const navigateToLogin = () => { sessionStorage.removeItem('resendVerificationEmail'); setCurrentPage('login'); };
  const navigateToRegister = () => { sessionStorage.removeItem('resendVerificationEmail'); setCurrentPage('register'); };
  const navigateToResendVerification = (email) => {
    if (email) {
      setResendVerificationEmail(email);
      sessionStorage.setItem('resendVerificationEmail', email);
    }
    setCurrentPage('resendVerification');
  };
  const navigateToForgotPassword = () => setCurrentPage('forgotPassword');
  const navigateToChangePassword = () => {
    setIsOtpFlow(false); // Reset OTP flow when manually navigating to change password
    setChangePasswordReturnPage(currentPage); // Save current page to return to
    setCurrentPage('changePassword');
  };
  const navigateBackFromChangePassword = () => {
    setCurrentPage(changePasswordReturnPage);
  };
  const navigateToLanding = () => setCurrentPage('landing');
  const navigateToVerifyEmail = () => setCurrentPage('verifyEmail');
  const navigateToCourseReview = () => setCurrentPage('courseReview');
  const navigateToCourseDetail = (course) => {
    setSelectedCourse(course);
    setCurrentPage('courseReviewDetail');
  };
  const navigateToMyPosts = () => {
    console.log('navigateToMyPosts called, changing page to myPosts');
    setCurrentPage('myPosts');
  };
  const navigateToMeinMarktplatz = (tab = null) => { // Issue #65, #87
    console.log('navigateToMeinMarktplatz called, changing page to meinMarktplatz, tab:', tab);
    if (tab) {
      setMeinMarktplatzTab(tab);
    }
    setCurrentPage('meinMarktplatz');
  };
  const navigateToMarketplaceDetail = async (marketplaceObjectOrId) => {
    // Issue #87: Handle both full object and just ID (from notifications)
    if (typeof marketplaceObjectOrId === 'string') {
      // It's an ID, fetch the full object
      try {
        // First try marketplace endpoint
        let response = await axios.get(`/api/marketplace?limit=500&offset=0`);
        let marketplaceObject = response.data.find(item => item.id === marketplaceObjectOrId);

        // If not found, try user's own posts (for notifications about own objects)
        if (!marketplaceObject) {
          response = await axios.get(`/api/posts/my-posts?limit=500&offset=0`);
          marketplaceObject = response.data.find(item => item.id === marketplaceObjectOrId);
        }

        if (marketplaceObject) {
          setSelectedMarketplaceObject(marketplaceObject);
          setLandingPageTab('market');
          setCurrentPage('marketplaceDetail');
        } else {
          console.error('Marketplace object not found:', marketplaceObjectOrId);
        }
      } catch (err) {
        console.error('Failed to fetch marketplace object:', err);
      }
    } else {
      // It's a full object
      setSelectedMarketplaceObject(marketplaceObjectOrId);
      setLandingPageTab('market');
      setCurrentPage('marketplaceDetail');
    }
  };
  const navigateBackToMarketplace = () => {
    setLandingPageTab('market');
    setCurrentPage('landing');
  };

  // Issue #70: Proper back navigation using browser history
  const navigateBack = () => {
    console.log('[BACK NAVIGATION] navigateBack called - using window.history.back()');
    window.history.back();
  };

  // Issue #69: Navigate to seller contact page
  const navigateToSellerContact = (marketplaceObject) => {
    setSelectedMarketplaceObject(marketplaceObject);
    setCurrentPage('sellerContact');
  };

  // Issue #69: Navigate to chat page
  // Issue #430: fromSellerContact = true when arriving after sending a first
  // message, so the chat "back" button returns to the chat list instead of the
  // contact-seller page the buyer just left.
  const navigateToChat = (conversationId, fromSellerContact = false) => {
    setSelectedConversationId(conversationId);
    setChatFromSellerContact(fromSellerContact);
    setCurrentPage('chat');
  };

  // Issue #91: Navigate to chat list page (message tab)
  const navigateToChatList = () => {
    setCurrentPage('chatList');
  };

  // Issue #87: Navigate to notifications page
  const navigateToNotifications = () => {
    setCurrentPage('notifications');
  };

  // Issue #84: Navigate to profile page
  const navigateToProfile = () => {
    setCurrentPage('profile');
  };

  // Issue #79: Navigate to language settings page
  const navigateToLanguageSettings = () => {
    setCurrentPage('languageSettings');
  };

  // Issue #98: Navigate to display settings page (dark mode)
  const navigateToDisplaySettings = () => {
    setCurrentPage('displaySettings');
  };

  // Issue #88: Navigate to notification settings page
  const navigateToNotificationSettings = () => {
    setCurrentPage('notificationSettings');
  };

  // Issue #137: Navigate to account settings page
  const navigateToAccountSettings = () => {
    setCurrentPage('accountSettings');
  };

  // Issue #137: Navigate to delete account page
  const navigateToDeleteAccount = () => {
    setCurrentPage('deleteAccount');
  };

  // Issue #101: Navigate to create post page
  const navigateToCreatePost = () => {
    setCurrentPage('createPost');
  };

  // Issue #101: Navigate to create marketplace object page
  const navigateToCreateMarketplace = () => {
    setCurrentPage('createMarketplace');
  };

  // Issue #68: Navigate to privacy policy page
  const navigateToPrivacyPolicy = () => {
    setCurrentPage('privacyPolicy');
  };

  // Issue #118: Navigate to terms and conditions page
  const navigateToTermsAndConditions = () => {
    setCurrentPage('termsAndConditions');
  };

  // Issue #149: Navigate to community guideline page
  const navigateToCommunityGuideline = () => {
    setCurrentPage('communityGuideline');
  };

  const handleLoginSuccess = (otpLogin = false) => {
    // Re-register push notifications after login
    if (pushNotificationService.isPushNotificationsSupported()) {
      pushNotificationService.registerForPushNotifications();
    }

    // Issue #18: If user logged in with OTP, redirect to change password page
    if (otpLogin) {
      setIsOtpFlow(true);
      setCurrentPage('changePassword');
    } else {
      // Normal login - go to landing page
      setIsOtpFlow(false);
      setCurrentPage('landing');
    }
  };

  const handleLogout = async () => {
    // Unsubscribe browser from web push before session is destroyed
    if (pushNotificationService.webPushService && pushNotificationService.webPushService.isSupported()) {
      try {
        await pushNotificationService.webPushService.unsubscribe();
      } catch (e) {
        console.warn('[PUSH] Web push unsubscribe failed:', e);
      }
    }
    // Issue #87: Unregister push notification token on logout
    const fcmToken = pushNotificationService.getStoredToken();
    if (fcmToken) {
      await pushNotificationService.unregisterTokenFromBackend(fcmToken);
      pushNotificationService.clearStoredToken();
    }
    try {
      // Call logout API to invalidate session (also cleans up DB-side push subscriptions)
      await axios.post('/api/logout');
    } catch (error) {
      console.error('Logout error:', error);
      // Continue to login page even if logout fails
    }
    // Issue #90: Clear mobile session token if on native platform
    await mobileLogout();
    // Clear all saved state
    sessionStorage.removeItem('pageState');
    sessionStorage.removeItem('selectedMarketplaceObject');
    // Issue #378: Reset first-login suppression so that the next account login
    // session starts fresh; subsequent logins display the interstitial as usual.
    sessionStorage.removeItem('firstLoginInterstitialSuppressed');
    setCurrentPage('login');
  };

  // Check for existing session on page load
  useEffect(() => {
    const checkSession = async () => {
      // Check if URL has verification token or reset password path
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const isResetPasswordPath = window.location.pathname === '/reset-password';

      // Issue #312: Check for share URL paths (/share/post/<id>, /share/object/<id>, /share/course/<id>)
      const shareMatch = window.location.pathname.match(/^\/share\/(post|object|course)\/([a-f0-9-]+)$/i);
      if (shareMatch) {
        const [, shareType, shareId] = shareMatch;
        console.log('[SHARE] Detected share URL:', shareType, shareId);
        setPendingShareNav({ type: shareType, id: shareId });
        // Clear the URL to prevent re-processing
        window.history.replaceState({}, '', '/');
      }

      if (isResetPasswordPath && token) {
        // User is trying to reset password from admin-sent link
        setCurrentPage('resetPassword');
        setIsCheckingSession(false);
        return;
      }

      if (token && !isResetPasswordPath) {
        // User is trying to verify email, show verification page
        setCurrentPage('verifyEmail');
        setIsCheckingSession(false);
        return;
      }

      try {
        // Try to get user data - this will succeed if session cookie is valid
        await axios.get('/api/user/me');

        // Issue #87: Initialize push notifications on successful session check
        if (pushNotificationService.isPushNotificationsSupported()) {
          pushNotificationService.registerForPushNotifications();
        }

        // Session is valid - restore previous page state if exists
        // Issue #71: Check browser history state first (preserved on refresh), then sessionStorage
        let pageState = null;

        // First, try to get state from browser history (preserved on page refresh)
        if (window.history.state && window.history.state.currentPage) {
          console.log('[PAGE REFRESH] Restoring state from browser history:', window.history.state);
          pageState = window.history.state;
        } else {
          // Fallback to sessionStorage
          const savedPageState = sessionStorage.getItem('pageState');
          if (savedPageState) {
            try {
              pageState = JSON.parse(savedPageState);
              console.log('[PAGE REFRESH] Restoring state from sessionStorage:', pageState);
            } catch (parseError) {
              console.error('Error parsing saved page state:', parseError);
            }
          }
        }

        if (pageState && pageState.currentPage) {
          // Restore dependent state FIRST before setting currentPage
          if (pageState.landingPageTab) setLandingPageTab(pageState.landingPageTab);
          if (pageState.marketplaceFilter) setMarketplaceFilter(pageState.marketplaceFilter);
          if (pageState.meinMarktplatzTab) setMeinMarktplatzTab(pageState.meinMarktplatzTab); // Issue #71: Restore MeinMarktplatz tab
          if (pageState.courseReviewFilters) setCourseReviewFilters(pageState.courseReviewFilters); // Restore course review filters
          if (pageState.courseReviewSortBy !== undefined) setCourseReviewSortBy(pageState.courseReviewSortBy); // Restore course review sort
          if (pageState.selectedCourse) setSelectedCourse(pageState.selectedCourse);
          if (pageState.selectedMarketplaceObject) setSelectedMarketplaceObject(pageState.selectedMarketplaceObject);
          if (pageState.selectedConversationId) setSelectedConversationId(pageState.selectedConversationId); // Issue #69: Restore conversation ID
          if (pageState.isOtpFlow !== undefined) setIsOtpFlow(pageState.isOtpFlow);
          // Set currentPage LAST to ensure all dependent state is ready
          setCurrentPage(pageState.currentPage);
        } else {
          // No saved state - go to landing page
          console.log('[PAGE REFRESH] No saved state found, defaulting to landing page');
          setCurrentPage('landing');
        }
      } catch (error) {
        // No valid session (401) or error
        // Check if user was on an unauthenticated page (register, forgotPassword, etc.)
        const unauthenticatedPages = ['login', 'register', 'forgotPassword', 'resendVerification', 'privacyPolicyPublic', 'termsAndConditionsPublic', 'resetPassword'];
        let previousPage = null;

        // Check browser history first
        if (window.history.state && window.history.state.currentPage) {
          previousPage = window.history.state.currentPage;
        }

        // If previous page was an unauthenticated page, stay there
        if (previousPage && unauthenticatedPages.includes(previousPage)) {
          console.log('[PAGE REFRESH] Staying on unauthenticated page:', previousPage);
          setCurrentPage(previousPage);
        } else {
          // Clear any saved state since session is invalid
          sessionStorage.removeItem('pageState');
          sessionStorage.removeItem('selectedMarketplaceObject');
          setCurrentPage('login');
        }
      } finally {
        // Done checking session
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, []); // Run once on component mount

  // Show loading state while checking session
  // Issue #165: Use branded LoadingPage component
  // Issue #183: Only show if loading takes more than 1 second (showLoadingPage is set after 1s delay)
  if (isCheckingSession && showLoadingPage) {
    return <LoadingPage />;
  }

  // Issue #183: If still checking session but loading page not shown yet, render nothing
  // This prevents brief flash of content before session is verified
  if (isCheckingSession) {
    return null;
  }

  // Issue #89: Navigation context value - all navigation functions in one object
  // This eliminates prop drilling and allows components to access navigation via useNavigation hook
  // Uses 'onNavigateTo*' naming convention to match shared component prop expectations
  const navigationValue = {
    // Auth navigation
    onNavigateToLogin: navigateToLogin,
    onNavigateToRegister: navigateToRegister,
    onNavigateToForgotPassword: navigateToForgotPassword,
    // Main page navigation (used by AppHeader, HamburgerMenu, BottomNavigation)
    onNavigateToLandingPage: navigateToLanding,
    onNavigateToProfile: navigateToProfile,
    onNavigateToChangePassword: navigateToChangePassword,
    onNavigateToLanguageSettings: navigateToLanguageSettings,
    onNavigateToDisplaySettings: navigateToDisplaySettings, // Issue #98
    onNavigateToNotificationSettings: navigateToNotificationSettings, // Issue #88
    onNavigateToAccountSettings: navigateToAccountSettings, // Issue #137
    onNavigateToDeleteAccount: navigateToDeleteAccount, // Issue #137
    onNavigateToCourseReview: navigateToCourseReview,
    onNavigateToCourseDetail: navigateToCourseDetail,
    onNavigateToMyPosts: navigateToMyPosts,
    onNavigateToMeinMarktplatz: navigateToMeinMarktplatz,
    onNavigateToMarketplaceDetail: navigateToMarketplaceDetail,
    onNavigateToSellerContact: navigateToSellerContact,
    onNavigateToChat: navigateToChat,
    onNavigateToChatList: navigateToChatList,
    onNavigateToNotifications: navigateToNotifications,
    // Issue #87, #177: Navigate to post from notification
    // If openComments is true, navigate to MyPosts (user's own posts) to show the comment
    onNavigateToPost: (postId, openComments = false) => {
      console.log('Navigating to post:', postId, 'openComments:', openComments);
      if (openComments) {
        // Issue #177: Navigate to MyPosts for comment notifications (user's own posts)
        setMyPostsOpenCommentsForPostId(postId);
        navigateToMyPosts();
      } else {
        navigateToLanding();
      }
    },
    // Create dialogs (used by CreateOptionsMenu)
    onNavigateToCreatePost: navigateToCreatePost,
    onNavigateToCreateMarketplace: navigateToCreateMarketplace,
    // Issue #68: Privacy policy page
    onNavigateToPrivacyPolicy: navigateToPrivacyPolicy,
    // Issue #118: Terms and conditions page
    onNavigateToTermsAndConditions: navigateToTermsAndConditions,
    // Issue #149: Community guideline page
    onNavigateToCommunityGuideline: navigateToCommunityGuideline,
    // Utility
    onNavigateBack: navigateBack,
    onLogout: handleLogout
  };

  return (
    <ThemeProvider>
    <LanguageProvider>
    <AdProvider>
    <NavigationProvider navigation={navigationValue}>
      <GlobalModalProvider>
        <div className="App">
      {currentPage === 'login' && (
        <Login
          onNavigateToRegister={navigateToRegister}
          onNavigateToForgotPassword={navigateToForgotPassword}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      {currentPage === 'register' && (
        <Register
          onNavigateToLogin={navigateToLogin}
          onNavigateToResendVerification={navigateToResendVerification}
          onNavigateToPrivacyPolicy={() => setCurrentPage('privacyPolicyPublic')}
          onNavigateToTermsAndConditions={() => setCurrentPage('termsAndConditionsPublic')}
        />
      )}
      {/* Issue #68: Privacy Policy for unauthenticated users (from register page) */}
      {currentPage === 'privacyPolicyPublic' && (
        <PrivacyPolicy
          onNavigateBack={() => setCurrentPage('register')}
          isAuthenticated={false}
        />
      )}
      {/* Issue #368: Terms and Conditions (AGB) for unauthenticated users (from register page) */}
      {currentPage === 'termsAndConditionsPublic' && (
        <TermsAndConditions
          onNavigateBack={() => setCurrentPage('register')}
        />
      )}
      {currentPage === 'resendVerification' && (
        <ResendVerification
          email={resendVerificationEmail}
          onNavigateToRegister={navigateToRegister}
          onNavigateToLogin={navigateToLogin}
        />
      )}
      {currentPage === 'forgotPassword' && (
        <ForgotPassword
          navigateToLogin={navigateToLogin}
        />
      )}
      {currentPage === 'landing' && (
        <LandingPage
          initialTab={landingPageTab}
          initialMarketplaceFilter={marketplaceFilter}
          onMarketplaceFilterChange={setMarketplaceFilter}
          autoOpenCreatePost={autoOpenCreatePost}
          autoOpenCreateMarketplace={autoOpenCreateMarketplace}
          onAutoOpenHandled={() => { setAutoOpenCreatePost(false); setAutoOpenCreateMarketplace(false); }}
          openCommentsForPostId={openCommentsForPostId}
          onOpenCommentsHandled={() => setOpenCommentsForPostId(null)}
          sharedPost={sharedPost}
          onSharedPostHandled={() => setSharedPost(null)}
        />
      )}
      {currentPage === 'changePassword' && (
        <ChangePassword
          onNavigateToLanding={navigateToLanding}
          onNavigateBack={navigateBackFromChangePassword}
          onLogout={handleLogout}
          isOtpFlow={isOtpFlow}
        />
      )}
      {currentPage === 'verifyEmail' && (
        <EmailVerification
          onBackToLogin={navigateToLogin}
        />
      )}
      {currentPage === 'resetPassword' && (
        <ResetPasswordWithToken
          onNavigateToLogin={navigateToLogin}
        />
      )}
      {currentPage === 'courseReview' && (
        <CourseReview
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToChangePassword={navigateToChangePassword}
          onNavigateToCourseDetail={navigateToCourseDetail}
          onNavigateToMyPosts={navigateToMyPosts}
          onNavigateToMeinMarktplatz={navigateToMeinMarktplatz}
          onNavigateToCourseReview={navigateToCourseReview}
          initialFilters={courseReviewFilters}
          onFiltersChange={setCourseReviewFilters}
          initialSortBy={courseReviewSortBy}
          onSortChange={setCourseReviewSortBy}
          onLogout={handleLogout}
        />
      )}
      {currentPage === 'myPosts' && (
        <MyPosts
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToChangePassword={navigateToChangePassword}
          onNavigateToMyPosts={navigateToMyPosts}
          onNavigateToMeinMarktplatz={navigateToMeinMarktplatz}
          onNavigateToCourseReview={navigateToCourseReview}
          onLogout={handleLogout}
          openCommentsForPostId={myPostsOpenCommentsForPostId}
          onOpenCommentsHandled={() => setMyPostsOpenCommentsForPostId(null)}
        />
      )}
      {currentPage === 'meinMarktplatz' && ( // Issue #65
        <MeinMarktplatz
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToChangePassword={navigateToChangePassword}
          onNavigateToMarketplaceDetail={navigateToMarketplaceDetail}
          onNavigateToMyPosts={navigateToMyPosts}
          onNavigateToCourseReview={navigateToCourseReview}
          initialTab={meinMarktplatzTab} // Issue #71: Pass initial tab for page refresh
          onTabChange={setMeinMarktplatzTab} // Issue #71: Track tab changes
          onLogout={handleLogout}
        />
      )}
      {currentPage === 'courseReviewDetail' && (
        <CourseReviewDetail
          course={selectedCourse}
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToCourseReview={navigateToCourseReview}
          onNavigateToMyPosts={navigateToMyPosts}
          onNavigateToMeinMarktplatz={navigateToMeinMarktplatz}
          onNavigateToChangePassword={navigateToChangePassword}
          onNavigateBack={navigateBack}
          onLogout={handleLogout}
        />
      )}
      {currentPage === 'marketplaceDetail' && (
        <MarketplaceDetailPage
          marketplaceObject={selectedMarketplaceObject}
          onNavigateToLandingPage={navigateBackToMarketplace}
          onNavigateToChangePassword={navigateToChangePassword}
          onNavigateToMyPosts={navigateToMyPosts}
          onNavigateToMeinMarktplatz={navigateToMeinMarktplatz}
          onNavigateToCourseReview={navigateToCourseReview}
          onNavigateBack={navigateBack}
          onNavigateToSellerContact={navigateToSellerContact}
          onLogout={handleLogout}
        />
      )}
      {currentPage === 'sellerContact' && (
        <SellerContact
          marketplaceObject={selectedMarketplaceObject}
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToChat={navigateToChat}
          onNavigateBack={navigateBack}
          onLogout={handleLogout}
        />
      )}
      {currentPage === 'chat' && (
        <Chat
          conversationId={selectedConversationId}
          cameFromSellerContact={chatFromSellerContact}
          onNavigateToLandingPage={navigateToLanding}
          onNavigateBack={navigateBack}
          onLogout={handleLogout}
        />
      )}
      {/* Issue #91, #92, #93: Chat list page (message tab) */}
      {currentPage === 'chatList' && <ChatList />}
      {/* Issue #87: Notifications page */}
      {currentPage === 'notifications' && <NotificationList />}
      {currentPage === 'profile' && <Profile />}
      {currentPage === 'languageSettings' && (
        <LanguageSettings
          onNavigateToLandingPage={navigateToLanding}
          onNavigateToProfile={navigateToProfile}
          onNavigateBack={navigateBack}
          onNavigateToCreatePost={navigateToCreatePost}
          onNavigateToCreateMarketplace={navigateToCreateMarketplace}
          onLogout={handleLogout}
        />
      )}
      {/* Issue #98: Display Settings (Dark Mode) */}
      {currentPage === 'displaySettings' && <DisplaySettings />}
      {/* Issue #88: Notification Settings */}
      {currentPage === 'notificationSettings' && <NotificationSettings />}
      {/* Issue #137: Account Settings Page */}
      {currentPage === 'accountSettings' && <AccountSettings />}
      {/* Issue #137: Delete Account Page */}
      {currentPage === 'deleteAccount' && <DeleteAccount />}
      {/* Issue #101: Create Post Page */}
      {currentPage === 'createPost' && <CreatePostPage />}
      {/* Issue #101: Create Marketplace Object Page */}
      {currentPage === 'createMarketplace' && <CreateMarketplaceObjectPage />}
      {/* Issue #68: Privacy Policy Page - Authenticated users */}
      {currentPage === 'privacyPolicy' && <AuthenticatedPrivacyPolicy />}
      {/* Issue #118: Terms and Conditions Page - Authenticated users */}
      {currentPage === 'termsAndConditions' && <AuthenticatedTermsAndConditions />}
      {/* Issue #149: Community Guideline Page - Authenticated users */}
      {currentPage === 'communityGuideline' && <AuthenticatedCommunityGuideline />}
        </div>
        <MarketplaceModal />
        {/* Issue #321: System announcements - shown to all users (logged in or not) */}
        <AnnouncementDialog />
        {/* Issue #204: GDPR consent dialog - shown when user is authenticated and hasn't made a choice */}
        {currentPage !== 'login' && currentPage !== 'register' && currentPage !== 'forgotPassword' && currentPage !== 'verifyEmail' && currentPage !== 'resendVerification' && currentPage !== 'resetPassword' && (
          <>
            <ConsentDialog />
            {/* Issue #275: Confirmation popover after accepting/declining consent */}
            <ConsentConfirmationDialog />
            {/* Issue #311: Interstitial ad on first daily app open */}
            {/* Issue #324: Don't show interstitial when share access error is displayed or share navigation pending */}
            {showInterstitialAd && !shareAccessError && !pendingShareNav && (
              <InterstitialAd onClose={() => setShowInterstitialAd(false)} />
            )}
          </>
        )}
        {/* Issue #312: Share access error modal */}
        {/* Issue #324: Use warning variant for access denied errors */}
        <SuccessModal
          isOpen={!!shareAccessError}
          onClose={() => setShareAccessError(null)}
          message={shareAccessError || ''}
          variant="warning"
        />
      </GlobalModalProvider>
    </NavigationProvider>
    </AdProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
